import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function BookingResult({ outcome }) {
  const [params] = useSearchParams();
  const bid = params.get("bid");
  const pid = params.get("pid");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bid) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bid}`);
        setBooking(data);
      } catch (err) {
        console.error("Failed to load booking:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [bid]);

  const success = outcome === "success";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-500">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
      <div className="max-w-md w-full text-center rounded-3xl border border-stone-200 bg-stone-50 p-10 shadow-sm">
        {success ? (
          <CheckCircle2 size={48} strokeWidth={1.2} className="mx-auto text-green-700" />
        ) : (
          <XCircle size={48} strokeWidth={1.2} className="mx-auto text-stone-500" />
        )}
        <h1
          data-testid={`booking-${outcome}-title`}
          className="font-serif text-3xl text-stone-900 mt-5"
        >
          {success ? (pid ? "Payment received" : "Booking confirmed") : (pid ? "Payment cancelled" : "Booking cancelled")}
        </h1>
        <p className="text-stone-600 mt-3 text-sm">
          {success
            ? pid
              ? "Your payment has been received. Your receipt is now available in your dashboard."
              : "Your session is locked in. Your coach has been notified and you'll receive a calendar invite if they have Google Calendar connected."
            : "No charge was made. You can try again any time."}
        </p>
        {booking && (
          <div className="mt-6 rounded-2xl bg-white border border-stone-200 p-4 text-left">
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500">
              {booking.service_type?.replace("_", " ")}
            </div>
            <div className="font-serif text-xl text-stone-900 mt-1">
              {booking.service_title}
            </div>
            <div className="text-sm text-stone-500 mt-1">
              with {booking.business_name} — ${booking.price}
            </div>
          </div>
        )}
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/dashboard">
            <Button
              data-testid="booking-go-dashboard"
              className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50"
            >
              Go to dashboard
            </Button>
          </Link>
          <Link to="/">
            <Button
              data-testid="booking-browse-more"
              variant="outline"
              className="rounded-full border-stone-300 text-stone-700 hover:bg-stone-100"
            >
              Back to home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
